// EPSE Data Validation and Quality Checks
import { EPSEQuarterData, DataQualityReport, DataQualityWarning } from '@/types/epse';

/**
 * Calculate expected number of quarters for a date range
 */
function calculateExpectedQuarters(startDate: Date, endDate: Date): number {
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffQuarters = Math.floor(diffMs / (15 * 60 * 1000));
  return diffQuarters;
}

/**
 * Validate quarter data and generate quality report
 */
export function validateQuarterData(
  quarterData: EPSEQuarterData[],
  annualKwhDeclared?: number
): DataQualityReport {
  const warnings: DataQualityWarning[] = [];
  
  if (quarterData.length === 0) {
    return {
      total_quarters: 0,
      expected_quarters: 0,
      missing_quarters: 0,
      missing_percent: 100,
      coverage_score: 0,
      annual_kwh_calculated: 0,
      annual_kwh_mismatch_percent: null,
      warnings: [{
        type: 'missing_data',
        severity: 'error',
        message: 'Geen kwartierdata beschikbaar',
      }],
    };
  }
  
  // Sort by timestamp
  const sorted = [...quarterData].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );
  
  const firstDate = new Date(sorted[0].start_time);
  const lastDate = new Date(sorted[sorted.length - 1].end_time);
  const expectedQuarters = calculateExpectedQuarters(firstDate, lastDate);
  const totalQuarters = quarterData.length;
  const missingQuarters = Math.max(0, expectedQuarters - totalQuarters);
  const missingPercent = expectedQuarters > 0 ? (missingQuarters / expectedQuarters) * 100 : 0;
  
  // Check for gaps
  const gaps: Array<{ start: string; end: string; quarters: number }> = [];
  for (let i = 1; i < sorted.length; i++) {
    const prevEnd = new Date(sorted[i - 1].end_time).getTime();
    const currStart = new Date(sorted[i].start_time).getTime();
    const gapMs = currStart - prevEnd;
    
    if (gapMs > 15 * 60 * 1000) { // More than 15 minutes
      const gapQuarters = Math.floor(gapMs / (15 * 60 * 1000));
      gaps.push({
        start: sorted[i - 1].end_time,
        end: sorted[i].start_time,
        quarters: gapQuarters,
      });
    }
  }
  
  if (gaps.length > 0) {
    const totalGapQuarters = gaps.reduce((sum, g) => sum + g.quarters, 0);
    warnings.push({
      type: 'gap',
      severity: totalGapQuarters > 96 ? 'warning' : 'info',
      message: `${gaps.length} data gap(s) gevonden, totaal ${totalGapQuarters} ontbrekende kwartieren`,
      details: { gaps: gaps.slice(0, 5) }, // Show first 5 gaps
    });
  }
  
  // Missing data threshold warnings
  if (missingPercent > 20) {
    warnings.push({
      type: 'missing_data',
      severity: 'error',
      message: `Kritiek dataverlies: ${missingPercent.toFixed(1)}% van de kwartieren ontbreekt`,
    });
  } else if (missingPercent > 5) {
    warnings.push({
      type: 'missing_data',
      severity: 'warning',
      message: `${missingPercent.toFixed(1)}% van de kwartieren ontbreekt`,
    });
  }
  
  // Calculate annual kWh from quarter data
  const annualKwhCalculated = quarterData.reduce((sum, q) => sum + q.kw_value * 0.25, 0);
  
  // Check mismatch with declared annual kWh
  let mismatchPercent: number | null = null;
  if (annualKwhDeclared && annualKwhDeclared > 0) {
    // Annualize if less than a year of data
    const dataSpanDays = (lastDate.getTime() - firstDate.getTime()) / (24 * 60 * 60 * 1000);
    const annualizedKwh = dataSpanDays < 365 
      ? (annualKwhCalculated / dataSpanDays) * 365 
      : annualKwhCalculated;
    
    mismatchPercent = Math.abs((annualizedKwh - annualKwhDeclared) / annualKwhDeclared) * 100;
    
    if (mismatchPercent > 20) {
      warnings.push({
        type: 'mismatch',
        severity: 'warning',
        message: `Jaarverbruik wijkt ${mismatchPercent.toFixed(1)}% af van opgegeven waarde (berekend: ${Math.round(annualizedKwh).toLocaleString('nl-NL')} kWh vs opgegeven: ${annualKwhDeclared.toLocaleString('nl-NL')} kWh)`,
        details: {
          calculated: annualizedKwh,
          declared: annualKwhDeclared,
          data_span_days: dataSpanDays,
        },
      });
    }
  }
  
  // Check for outliers (values more than 3 standard deviations from mean)
  const values = quarterData.map(q => q.kw_value);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const stdDev = Math.sqrt(
    values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length
  );
  
  const outliers = quarterData.filter(q => Math.abs(q.kw_value - mean) > 3 * stdDev);
  if (outliers.length > 0) {
    warnings.push({
      type: 'outlier',
      severity: 'info',
      message: `${outliers.length} mogelijke uitschieters gedetecteerd (>3σ van gemiddelde)`,
      details: {
        count: outliers.length,
        threshold_kw: mean + 3 * stdDev,
        examples: outliers.slice(0, 3).map(o => ({
          timestamp: o.start_time,
          kw: o.kw_value,
        })),
      },
    });
  }
  
  // Coverage score (0-100)
  const coverageScore = Math.max(0, 100 - missingPercent - (gaps.length * 2));
  
  return {
    total_quarters: totalQuarters,
    expected_quarters: expectedQuarters,
    missing_quarters: missingQuarters,
    missing_percent: missingPercent,
    coverage_score: Math.round(coverageScore),
    annual_kwh_calculated: annualKwhCalculated,
    annual_kwh_mismatch_percent: mismatchPercent,
    warnings,
  };
}

/**
 * Parse Excel/CSV quarter data
 */
export function parseQuarterDataFromExcel(
  rows: Array<Record<string, unknown>>,
  columnMapping: {
    timestamp?: string;
    start_time?: string;
    end_time?: string;
    kw_value?: string;
    kwh_value?: string;
    pv_kw?: string;
    detected_unit?: 'kW' | 'kWh';
  }
): Omit<EPSEQuarterData, 'id' | 'site_id' | 'created_at'>[] {
  const result: Omit<EPSEQuarterData, 'id' | 'site_id' | 'created_at'>[] = [];
  const isKwh = !!columnMapping.kwh_value && !columnMapping.kw_value;
  
  rows.forEach((row, index) => {
    try {
      let startTime: Date;
      let endTime: Date;
      
      if (columnMapping.start_time && row[columnMapping.start_time]) {
        startTime = parseExcelDate(row[columnMapping.start_time]);
        if (columnMapping.end_time && row[columnMapping.end_time]) {
          endTime = parseExcelDate(row[columnMapping.end_time]);
        } else {
          endTime = new Date(startTime.getTime() + 15 * 60 * 1000);
        }
      } else if (columnMapping.timestamp && row[columnMapping.timestamp]) {
        startTime = parseExcelDate(row[columnMapping.timestamp]);
        endTime = new Date(startTime.getTime() + 15 * 60 * 1000);
      } else {
        console.warn(`Row ${index}: No timestamp found`);
        return;
      }
      
      // Get the value from either kW or kWh column
      const valueColumn = columnMapping.kw_value || columnMapping.kwh_value;
      const valueRaw = valueColumn 
        ? row[valueColumn] 
        : Object.values(row).find(v => typeof v === 'number' && v > 0);
      
      let kwValue = parseNumberValue(valueRaw);
      if (kwValue === null) {
        console.warn(`Row ${index}: Invalid value`);
        return;
      }
      
      // Convert kWh to kW: kWh per 15min × 4 = kW average
      if (isKwh) {
        kwValue = kwValue * 4;
      }
      
      let pvKw: number | null = null;
      if (columnMapping.pv_kw && row[columnMapping.pv_kw]) {
        pvKw = parseNumberValue(row[columnMapping.pv_kw]);
      }
      
      result.push({
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        kw_value: kwValue,
        value_type: 'avg',
        pv_kw: pvKw,
        time_context: {} as any, // Will be filled later
      });
    } catch (error) {
      console.warn(`Row ${index}: Parse error`, error);
    }
  });
  
  return result;
}

/**
 * Parse Excel date (handles serial numbers and strings)
 */
function parseExcelDate(value: unknown): Date {
  if (value instanceof Date) return value;
  
  if (typeof value === 'number') {
    // Excel serial date (days since 1900-01-01, with leap year bug)
    const excelEpoch = new Date(1899, 11, 30);
    return new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
  }
  
  if (typeof value === 'string') {
    // Try European format first (D-M-YYYY HH:mm:ss or D-M-YYYY HH:mm)
    const euroMatch = value.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (euroMatch) {
      const [, day, month, year, hour, minute, second] = euroMatch.map(Number);
      const fullYear = year < 100 ? 2000 + year : year;
      return new Date(fullYear, month - 1, day, hour, minute, second || 0);
    }
    
    // Try standard ISO format
    const date = new Date(value);
    if (!isNaN(date.getTime())) return date;
  }
  
  throw new Error(`Cannot parse date: ${value}`);
}

/**
 * Parse number value (handles European format with comma)
 */
function parseNumberValue(value: unknown): number | null {
  if (typeof value === 'number') return value;
  
  if (typeof value === 'string') {
    // Replace comma with dot for European format
    const normalized = value.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(normalized);
    return isNaN(num) ? null : num;
  }
  
  return null;
}

/**
 * Detect column mapping from Excel headers
 */
export function detectColumnMapping(headers: string[]): {
  timestamp?: string;
  start_time?: string;
  end_time?: string;
  kw_value?: string;
  kwh_value?: string;
  pv_kw?: string;
  detected_unit?: 'kW' | 'kWh';
} {
  const mapping: Record<string, string | undefined> = {};
  
  const lowerHeaders = headers.map(h => h.toLowerCase().trim());
  
  // Timestamp patterns - includes 'van' for Dutch "Van" column
  const timestampPatterns = ['timestamp', 'tijd', 'time', 'datum', 'date', 'begintijd', 'start', 'van', 'from', 'begin'];
  // End time patterns - includes 'tot' for Dutch "Tot" column
  const endTimePatterns = ['eindtijd', 'end', 'end_time', 'endtime', 'tot', 'to', 'eind'];
  // kW patterns (instantaneous power)
  const kwPatterns = ['kw', 'power', 'vermogen', 'piek', 'peak', 'belasting', 'load'];
  // kWh patterns (energy per interval)
  const kwhPatterns = ['kwh', 'energie', 'energy', 'consumption', 'verbruik', 'afname'];
  // PV patterns
  const pvPatterns = ['pv', 'solar', 'zon', 'opwek', 'generation', 'teruglevering'];
  
  headers.forEach((header, i) => {
    const lower = lowerHeaders[i];
    
    if (timestampPatterns.some(p => lower.includes(p)) && !mapping.start_time) {
      mapping.start_time = header;
    }
    if (endTimePatterns.some(p => lower.includes(p)) && !mapping.end_time) {
      mapping.end_time = header;
    }
    // Check kWh first (more specific)
    if (kwhPatterns.some(p => lower.includes(p)) && !mapping.kwh_value && !mapping.kw_value) {
      mapping.kwh_value = header;
      mapping.detected_unit = 'kWh';
    }
    // Then check kW
    if (kwPatterns.some(p => lower.includes(p)) && !lower.includes('kwh') && !mapping.kw_value) {
      mapping.kw_value = header;
      mapping.detected_unit = 'kW';
    }
    if (pvPatterns.some(p => lower.includes(p)) && !mapping.pv_kw) {
      mapping.pv_kw = header;
    }
  });
  
  // Default to kW if no unit detected but we have a value column
  if ((mapping.kw_value || mapping.kwh_value) && !mapping.detected_unit) {
    mapping.detected_unit = mapping.kwh_value ? 'kWh' : 'kW';
  }
  
  return mapping as ReturnType<typeof detectColumnMapping>;
}
